import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { resetUserPassword } from "api/users";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  notify,
  PasswordInput,
} from "Components/ui";
import { useAuth } from "hooks/useAuth";

const schema = z.object({
  password: z.string().min(8, "Use at least 8 characters."),
});

type ResetValues = z.infer<typeof schema>;

interface ResetPasswordModalProps {
  open: boolean;
  userId: string | null;
  /** Shown in the description. */
  userName?: string;
  onClose: () => void;
  onSaved: () => void;
}

/** Sets a new password for another user (PUT /api/rbac/users/:id with `password`). */
const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  userId,
  userName,
  onClose,
  onSaved,
}) => {
  const { hasPermission } = useAuth();
  const canConfigure = hasPermission("userManagement", "configure");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ resolver: zodResolver(schema), defaultValues: { password: "" } });

  React.useEffect(() => {
    if (open) {
      reset({ password: "" });
    }
  }, [open, reset]);

  const onSubmit = async ({ password }: ResetValues) => {
    if (!canConfigure) {
      notify.error("You don't have permission to reset passwords.");
      return;
    }
    if (!userId) {
      return;
    }
    try {
      await resetUserPassword(userId, password);
      notify.success("Password reset");
      onSaved();
    } catch (err) {
      notify.error(err, { fallback: "The password wasn't changed." });
    }
  };

  return (
    <Dialog
      open={open && Boolean(userId) && canConfigure}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) {
          onClose();
        }
      }}
    >
      <DialogContent size="sm">
        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-col">
          <DialogHeader
            title="Reset password"
            description={`${
              userName || "This user"
            } signs in with the new password from now on. The current one stops working.`}
          />
          <DialogBody>
            <FormField
              label="New password"
              required
              error={errors.password?.message}
              hint="At least 8 characters. Share it with them privately."
            >
              <PasswordInput autoComplete="new-password" {...register("password")} />
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" loading={isSubmitting}>
              Reset password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordModal;
